import {
  forwardRef,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';

import { cn } from '@/utils/classname';

export interface ContainerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ReactNode;
  className?: string;

  /** Visual variant: 'default' (1px border, no bg) or 'emphasized' (2px border, semi-transparent bg) */
  variant?: 'default' | 'emphasized';

  /** Enable selection state styling (border-brand-primary when selected) */
  isSelected?: boolean;

  /** Auto-focus container when id prop changes (accessibility feature) */
  autoFocusOnIdChange?: boolean;

  /** Render as semantic element */
  as?: 'div' | 'section';
}

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      children,
      className,
      variant = 'default',
      isSelected = false,
      autoFocusOnIdChange = false,
      as: Element = 'div',
      id,
      onClick,
      ...props
    },
    forwardedRef
  ) => {
    // Auto-focus logic (from @praetorian-chariot/ui)
    const localRef = useRef<HTMLDivElement>(null);
    const ref = (forwardedRef ||
      localRef) as MutableRefObject<HTMLDivElement | null>;

    useEffect(() => {
      if (autoFocusOnIdChange && ref.current) {
        ref.current.focus();
      }
    }, [id, autoFocusOnIdChange, ref]);

    // Variant styles
    const baseStyles = 'rounded-md focus:outline-none';
    const borderStyles =
      variant === 'default'
        ? 'border border-chariot-stroke'
        : 'border-2 border-chariot-stroke';
    const backgroundStyles =
      variant === 'default' ? '' : 'bg-chariot-background-primary/60';
    const paddingStyles = 'p-6';
    const selectionStyles = isSelected
      ? 'border-chariot-brand-primary bg-chariot-background-secondary/40'
      : '';
    const interactiveStyles = onClick ? 'cursor-pointer' : '';

    return (
      <Element
        ref={ref}
        id={id}
        className={cn(
          baseStyles,
          borderStyles,
          backgroundStyles,
          paddingStyles,
          selectionStyles,
          interactiveStyles,
          variant === 'emphasized' && 'relative',
          className
        )}
        onClick={onClick}
        {...props}
      >
        {children}
      </Element>
    );
  }
);

Container.displayName = 'Container';
